/**
 * VenueFlow LED Uploader Section Component
 * 
 * Specialized uploader for LED content with:
 * - Filename validation (must end with segment ID)
 * - Per-segment dimension validation
 * - Duration consistency enforcement
 * - Grouped asset status tracking
 * - Prevention of duplicate segments
 */

import React, { useState, useCallback, useRef, useEffect } from "react";
import DropArea from "./DropArea";
import FileList from "./FileList";
import FailedList from "./FailedList";
import { uploadToDropbox } from "../../services/dropbox";
import { validateFile as baseValidateFile } from "../../services/mediaInfo";
import { generateFileId } from "../../services/idGenerator";

// ============================================================
// STYLES
// ============================================================
const styles = {
  section: {
    margin: "14px 0 8px",
    padding: "14px 16px",
    borderRadius: 12,
    background: "#f9fafb",
    border: "1px solid #e5e7eb",
  },
  title: {
    margin: "0 0 4px",
    fontSize: 14,
    fontWeight: 600,
    color: "#111827",
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  status: {
    fontWeight: 500,
    fontSize: 13,
  },
  statusIncomplete: {
    color: "#6b7280",
  },
  statusComplete: {
    color: "#059669",
  },
  specLabel: {
    marginTop: 2,
    marginBottom: 4,
    fontSize: 13,
    color: "#6b7280",
    lineHeight: 1.4,
  },
  filenameHint: {
    fontSize: 12,
    color: "#777",
    marginTop: 2,
    marginBottom: 8,
    fontStyle: "italic",
  },
  queueBanner: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 12px",
    marginTop: 8,
    borderRadius: 8,
    background: "#eff6ff",
    border: "1px solid #bfdbfe",
    fontSize: 13,
    color: "#1e40af",
    fontWeight: 500,
  },
  queueSpinner: {
    width: 14,
    height: 14,
    border: "2px solid #bfdbfe",
    borderTopColor: "#3b82f6",
    borderRadius: "50%",
    animation: "led-queue-spin 0.8s linear infinite",
    flexShrink: 0,
  },
};

// ============================================================
// DURATION TOLERANCE
// ============================================================
const DURATION_TOLERANCE = 0.25; // seconds

// ============================================================
// LED UPLOADER SECTION COMPONENT
// ============================================================
export function LedUploaderSection({
  uploadId,
  spec,
  context,
  sessionId,
  submissionTimeId,
  onUploadComplete,
  onUploadFailed,
  onUploadStart,
  onUploadCancel,
  // For duration consistency across levels
  referenceDuration,
  onDurationDetected,
  // Spec helper functions (passed from parent to support MCG/Marvel)
  validateFilename,
  getDimensionsForFile,
  buildSpecLabel,
  // Venue prefix for Dropbox path (e.g., "MCG" or "Marvel")
  venuePrefix = "MCG",
  // Optional asset name for folder path (e.g., "CocaCola-15s")
  assetName = null,
}) {
  const [files, setFiles] = useState([]);
  const [queueInfo, setQueueInfo] = useState(null); // { total, processed }
  const [failed, setFailed] = useState([]);
  const [progress, setProgress] = useState({});
  const abortControllers = useRef(new Map());
  const pendingNames = useRef(new Set());
  const uploadedSegments = useRef(new Set());

  // Track local duration for this uploader
  const [localDuration, setLocalDuration] = useState(null);

  // Get status text
  const getStatusText = () => {
    const uploaded = files.filter((f) => f.status === "complete").length;
    const required = spec.requiredFiles ?? spec.maxFiles ?? 1;

    if (uploaded === 0) {
      return { text: `0 uploaded, ${required} remaining`, complete: false };
    } else if (uploaded < required) {
      return { text: `${uploaded} uploaded, ${required - uploaded} remaining`, complete: false };
    } else {
      return { text: "✅ complete", complete: true };
    }
  };

  // Build Dropbox path for LED
  const buildDropboxPath = useCallback(
    (prefixedFileName) => {
      const base = `/AFL_Content/_incoming/${context.submitterType}/${context.submitterName}/${sessionId}`;
      const rootPrefix = `${context.submitterShortName}_${submissionTimeId}`;

      // Grouped assets get a parent folder
      if (spec.contentGroup === "groupedAssets") {
        const assetSegment = assetName ? `${assetName}_` : "";
        const parentFolder = `${rootPrefix}_${assetSegment}${venuePrefix}-${spec.ledType}`;
        return `${base}/${parentFolder}/${prefixedFileName}`;
      }

      // Single assets go to session root
      return `${base}/${prefixedFileName}`;
    },
    [context, sessionId, submissionTimeId, spec, venuePrefix, assetName]
  );

  // Validate LED file with additional checks
  const validateLedFile = useCallback(
    async (file) => {
      const reasons = [];

      // 1. Filename validation
      const filenameResult = validateFilename(file.name, spec);
      if (!filenameResult.valid) {
        reasons.push(filenameResult.hint || `Invalid filename for ${spec.label}`);
      }

      // 2. Check for duplicate segment (for 360 specs)
      if (spec.segments && filenameResult.segmentId) {
        if (uploadedSegments.current.has(filenameResult.segmentId)) {
          reasons.push(
            `A file for "${filenameResult.segmentId}" has already been uploaded. Only one file per LED segment is allowed.`
          );
        }
      }

      // 3. Get expected dimensions for this file
      const expectedDims = getDimensionsForFile(file.name, spec);

      // 4. Build a modified spec for base validation
      const validationSpec = {
        ...spec,
        videoSpec: spec.videoSpec || spec.commonVideoSpec
          ? {
              ...(spec.videoSpec || spec.commonVideoSpec),
              width: expectedDims?.width,
              height: expectedDims?.height,
            }
          : null,
        imageSpec: spec.imageSpec
          ? {
              ...spec.imageSpec,
              width: expectedDims?.width,
              height: expectedDims?.height,
            }
          : null,
        dimensions: expectedDims ? [expectedDims] : null,
      };

      // 5. Run base validation (codec, framerate, dimensions)
      let baseResult;
      try {
        baseResult = await baseValidateFile(file, validationSpec);
      } catch (err) {
        console.error("Base validation error:", err);
        return {
          valid: false,
          reasons: ["File validation failed - please try again"],
          info: { type: null, width: null, height: null, frameRate: null, duration: null, format: null, alpha: { present: false, type: null }, audio: { present: false } },
          segmentId: filenameResult.segmentId,
        };
      }

      // Merge reasons
      if (!baseResult.valid) {
        reasons.push(...baseResult.reasons);
      }

      // 6. Duration validation
      if (baseResult.info?.type === "video" && baseResult.info?.duration) {
        const duration = baseResult.info.duration;

        // Exact duration check (interchange, ponsford)
        if (spec.exactDuration) {
          if (Math.abs(duration - spec.exactDuration) > DURATION_TOLERANCE) {
            reasons.push(
              `Duration must be exactly ${spec.exactDuration}s (this file is ${duration.toFixed(2)}s)`
            );
          }
        }
        // Range duration check (tiled, 360)
        else if (spec.durationRange) {
          if (duration < spec.durationRange.min - DURATION_TOLERANCE) {
            reasons.push(
              `Duration must be at least ${spec.durationRange.min}s (this file is ${duration.toFixed(2)}s)`
            );
          }
          if (duration > spec.durationRange.max + DURATION_TOLERANCE) {
            reasons.push(
              `Duration must be at most ${spec.durationRange.max}s (this file is ${duration.toFixed(2)}s)`
            );
          }
        }

        // Duration consistency check (all files in group must match)
        const refDuration = referenceDuration || localDuration;
        if (refDuration && Math.abs(refDuration - duration) > DURATION_TOLERANCE) {
          reasons.push(
            `All LED files must have the same duration. First file: ${refDuration.toFixed(2)}s, this file: ${duration.toFixed(2)}s`
          );
        }
      }

      // 7. Check for mixing image/video types in grouped assets
      if (spec.contentGroup === "groupedAssets" && files.length > 0) {
        const firstType = files[0]?.inspection?.type;
        const thisType = baseResult.info?.type;

        if (firstType && thisType && firstType !== thisType) {
          reasons.push(
            `This bucket already contains ${firstType}s. Images and videos cannot be mixed.`
          );
        }
      }

      return {
        valid: reasons.length === 0,
        reasons,
        info: baseResult.info,
        segmentId: filenameResult.segmentId,
        ...(baseResult.browserFallback && { browserFallback: true }),
        ...(baseResult.uncheckedFields && { uncheckedFields: baseResult.uncheckedFields }),
      };
    },
    [spec, files, referenceDuration, localDuration, validateFilename, getDimensionsForFile]
  );

  // Handle file selection
  const handleFiles = useCallback(
    async (selectedFiles) => {
      const totalFiles = selectedFiles.length;
      if (totalFiles > 1) {
        setQueueInfo({ total: totalFiles, processed: 0 });
      }

      for (const file of selectedFiles) {
        try {
        const duplicateKey = `${uploadId}::${file.name.toLowerCase()}`;

        // Check for duplicates
        if (pendingNames.current.has(duplicateKey)) {
          setFailed((prev) => [
            ...prev,
            { file, reasons: [`Duplicate filename "${file.name}"`] },
          ]);
          continue;
        }
        pendingNames.current.add(duplicateKey);

        // Check max files
        if (typeof spec.maxFiles === "number") {
          const currentCount = files.length;
          if (currentCount >= spec.maxFiles) {
            pendingNames.current.delete(duplicateKey);
            setFailed((prev) => [
              ...prev,
              { file, reasons: [`This bucket allows a maximum of ${spec.maxFiles} files`] },
            ]);
            continue;
          }
        }

        // Build prefixed filename early so we can show the file immediately
        const prefixedFileName = `${context.submitterShortName}_${submissionTimeId}_${file.name}`;
        const fileId = generateFileId(uploadId, prefixedFileName);
        const path = buildDropboxPath(prefixedFileName);

        // Add file immediately with "validating" status so the user sees it
        const validatingEntry = {
          id: fileId,
          uploadId,
          originalFileName: file.name,
          prefixedFileName,
          displayName: `${file.name} → ${prefixedFileName}`,
          status: "validating",
          file,
          inspection: null,
          segmentId: null,
        };
        setFiles((prev) => [...prev, validatingEntry]);

        // Validate file (may take a while for large files via Web Worker)
        let result;
        try {
          result = await validateLedFile(file);
        } catch (err) {
          console.error("Validation error:", err);
          pendingNames.current.delete(duplicateKey);
          setFiles((prev) => prev.filter((f) => f.id !== fileId));
          setFailed((prev) => [...prev, { file, reasons: ["File validation failed - please try again"] }]);
          onUploadFailed?.({ uploadId, file, reasons: ["File validation failed"] });
          continue;
        }
        
        if (!result.valid) {
          pendingNames.current.delete(duplicateKey);
          setFiles((prev) => prev.filter((f) => f.id !== fileId));
          setFailed((prev) => [...prev, { file, reasons: result.reasons }]);
          onUploadFailed?.({ uploadId, file, reasons: result.reasons });
          continue;
        }

        // Track segment for 360 specs
        if (result.segmentId) {
          uploadedSegments.current.add(result.segmentId);
        }

        // Track duration for consistency
        if (result.info?.type === "video" && result.info?.duration && !localDuration) {
          setLocalDuration(result.info.duration);
          onDurationDetected?.(result.info.duration);
        }

        // Transition to "uploading" status
        setFiles((prev) =>
          prev.map((f) =>
            f.id === fileId
              ? {
                  ...f,
                  status: "uploading",
                  inspection: result.info,
                  segmentId: result.segmentId,
                  ...(result.browserFallback && { browserFallback: true }),
                  ...(result.uncheckedFields && { uncheckedFields: result.uncheckedFields }),
                }
              : f
          )
        );
        onUploadStart?.({ uploadId, file: { ...validatingEntry, status: "uploading", inspection: result.info, segmentId: result.segmentId } });

        // Create abort controller
        const abortController = new AbortController();
        abortControllers.current.set(fileId, abortController);

        // Upload to Dropbox
        try {
          const url = await uploadToDropbox(
            file,
            path,
            (pct) => {
              setProgress((prev) => ({ ...prev, [fileId]: pct }));
            },
            abortController
          );

          // Build file metadata
          const fileMeta = {
            id: fileId,
            uploadId,
            ledType: spec.ledType,
            level: spec.level,
            segmentId: result.segmentId,
            label: spec.label,
            contentGroup: spec.contentGroup,
            sourcePath: path,
            sourcePathParent: path.split("/").slice(0, -1).join("/"),
            submissionParent: `/AFL_Content/_incoming/${context.submitterType}/${context.submitterName}`,
            originalFileName: file.name,
            prefixedFileName,
            submitterType: context.submitterType,
            submitterName: context.submitterName,
            submitterShortName: context.submitterShortName,
            submissionTimeId,
            fileType: file.type,
            size: file.size,
            url,
            inspection: result.info,
            ...(result.browserFallback && { browserFallback: true }),
            ...(result.uncheckedFields && { uncheckedFields: result.uncheckedFields }),
          };

          // Update file status
          setFiles((prev) =>
            prev.map((f) =>
              f.id === fileId ? { ...f, status: "complete", meta: fileMeta } : f
            )
          );

          // Clear progress after delay
          setTimeout(() => {
            setProgress((prev) => {
              const next = { ...prev };
              delete next[fileId];
              return next;
            });
          }, 600);

          onUploadComplete?.({ uploadId, file: fileMeta });
        } catch (err) {
          console.error("Upload failed:", err);
          pendingNames.current.delete(duplicateKey);
          if (result.segmentId) {
            uploadedSegments.current.delete(result.segmentId);
          }
          setFiles((prev) => prev.filter((f) => f.id !== fileId));
          setFailed((prev) => [
            ...prev,
            { file, reasons: ["Upload cancelled or failed"] },
          ]);
        } finally {
          abortControllers.current.delete(fileId);
        }

        } finally {
          // Update queue progress (triggered by continue and normal flow)
          if (totalFiles > 1) {
            setQueueInfo((prev) =>
              prev ? { ...prev, processed: prev.processed + 1 } : null
            );
          }
        }
      }

      // Clear queue indicator
      setQueueInfo(null);
    },
    [
      uploadId,
      spec,
      files,
      context,
      submissionTimeId,
      buildDropboxPath,
      validateLedFile,
      localDuration,
      onUploadStart,
      onUploadComplete,
      onUploadFailed,
      onDurationDetected,
    ]
  );

  // Handle file cancellation
  const handleCancel = useCallback(
    (fileId) => {
      const controller = abortControllers.current.get(fileId);
      if (controller) {
        controller.abort();
      }

      const file = files.find((f) => f.id === fileId);
      if (file) {
        const duplicateKey = `${uploadId}::${file.originalFileName.toLowerCase()}`;
        pendingNames.current.delete(duplicateKey);

        // Remove from uploaded segments
        if (file.segmentId) {
          uploadedSegments.current.delete(file.segmentId);
        }
      }

      setFiles((prev) => prev.filter((f) => f.id !== fileId));
      onUploadCancel?.({ uploadId, fileId });
    },
    [files, uploadId, onUploadCancel]
  );

  // Clear failed list
  const handleClearFailed = useCallback(() => {
    setFailed([]);
  }, []);

  // Reset when spec changes
  useEffect(() => {
    setFiles([]);
    setFailed([]);
    setProgress({});
    setLocalDuration(null);
    setQueueInfo(null);
    pendingNames.current.clear();
    uploadedSegments.current.clear();
    abortControllers.current.forEach((c) => c.abort());
    abortControllers.current.clear();
  }, [uploadId]);

  const status = getStatusText();

  return (
    <div style={styles.section} data-upload-id={uploadId}>
      <h3 style={styles.title}>
        {spec.label}
        <span
          style={{
            ...styles.status,
            ...(status.complete ? styles.statusComplete : styles.statusIncomplete),
          }}
        >
          — {status.text}
        </span>
      </h3>
      <div style={styles.specLabel}>{buildSpecLabel(spec)}</div>
      {spec.filenameHint && (
        <div style={styles.filenameHint}>{spec.filenameHint}</div>
      )}

      <DropArea onFiles={handleFiles} multiple={spec.maxFiles > 1} />

      {queueInfo && (
        <>
          <style>{`@keyframes led-queue-spin { to { transform: rotate(360deg) } }`}</style>
          <div style={styles.queueBanner}>
            <div style={styles.queueSpinner} />
            Processing file {queueInfo.processed + 1} of {queueInfo.total}
            {queueInfo.total - queueInfo.processed - 1 > 0 && (
              <span style={{ fontWeight: 400, color: "#3b82f6" }}>
                — {queueInfo.total - queueInfo.processed - 1} queued
              </span>
            )}
          </div>
        </>
      )}

      <FileList files={files} progress={progress} onCancel={handleCancel} />

      <FailedList items={failed} onClear={handleClearFailed} />
    </div>
  );
}

export default LedUploaderSection;
